<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { LUCI_CONTROL_PERMISSIONS } from 'dashboard/constants/permissions';
import {
  hasAmbiguousPosition,
  isHumanControlled,
  isKnownMode,
  modeClass as computeModeClass,
  modeTranslationKey,
  pendingTransition,
} from 'dashboard/helper/luciControlHelper';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { isFeatureFlagEnabled, checkPermissions } = usePolicy();

// The platform is the source of truth for control, so this is never cached
// globally and never derived from assignee or status. It is read for the open
// conversation and re-read after every transition.
const control = ref(null);
const isLoading = ref(false);
const isUnavailable = ref(false);
// Guards against a slow response for a previously open conversation being
// applied to the one now on screen.
const requestToken = ref(0);

const isEntitled = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.LUCI_CONVERSATION_CONTROL)
);
const canChangeControl = computed(() =>
  checkPermissions(['administrator', LUCI_CONTROL_PERMISSIONS])
);

const mode = computed(() => control.value?.mode ?? null);
const isVisible = computed(
  () => isEntitled.value && !isUnavailable.value && isKnownMode(mode.value)
);
const isHuman = computed(() => isHumanControlled(mode.value));
// paused and technical_handoff are neither position, so the real state is
// shown alongside instead of being rounded to one side.
const showsRealState = computed(() => hasAmbiguousPosition(mode.value));
const stateLabel = computed(() => {
  const key = modeTranslationKey(mode.value);
  return key ? t(key) : '';
});
const stateClass = computed(() => computeModeClass(mode.value));
const nextTransition = computed(() =>
  pendingTransition({
    mode: mode.value,
    canChangeControl: canChangeControl.value,
  })
);
const isInteractive = computed(
  () => nextTransition.value !== null && !isLoading.value
);

const load = async () => {
  if (!isEntitled.value) return;
  requestToken.value += 1;
  const token = requestToken.value;
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.getLuciControl({
      conversationId: props.conversationId,
    });
    if (token !== requestToken.value) return;
    control.value = data?.payload ?? null;
    isUnavailable.value = false;
  } catch {
    if (token !== requestToken.value) return;
    control.value = null;
    isUnavailable.value = true;
  } finally {
    if (token === requestToken.value) isLoading.value = false;
  }
};

const handleFailure = error => {
  const status = error?.response?.status;
  // A conversation without control state came from the legacy path. Hide the
  // control instead of showing an error.
  if (status === 404) {
    isUnavailable.value = true;
    control.value = null;
    return;
  }
  if (status === 409) {
    useAlert(t('CONVERSATION.LUCI_CONTROL.ERROR.CONFLICT'));
    load();
    return;
  }
  if (status === 403) {
    useAlert(t('CONVERSATION.LUCI_CONTROL.ERROR.DENIED'));
    return;
  }
  useAlert(t('CONVERSATION.LUCI_CONTROL.ERROR.GENERIC'));
};

const toggle = async () => {
  if (!isInteractive.value || !control.value) return;
  const action =
    nextTransition.value === 'activate_luci'
      ? ConversationApi.activateLuci
      : ConversationApi.takeLuciControl;
  requestToken.value += 1;
  const token = requestToken.value;
  isLoading.value = true;
  try {
    const { data } = await action.call(ConversationApi, {
      conversationId: props.conversationId,
      expectedControlVersion: control.value.control_version,
    });
    if (token !== requestToken.value) return;
    control.value = data?.payload ?? null;
    isUnavailable.value = false;
  } catch (error) {
    if (token !== requestToken.value) return;
    handleFailure(error);
  } finally {
    if (token === requestToken.value) isLoading.value = false;
  }
};

watch(
  () => props.conversationId,
  () => {
    control.value = null;
    isUnavailable.value = false;
    load();
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="isVisible" class="flex gap-2 items-center">
    <span
      v-if="showsRealState"
      class="text-xs font-medium whitespace-nowrap"
      :class="stateClass"
    >
      {{ stateLabel }}
    </span>
    <button
      type="button"
      role="switch"
      :aria-checked="isHuman"
      :aria-label="$t('CONVERSATION.LUCI_CONTROL.ARIA_LABEL')"
      :disabled="!isInteractive"
      class="flex relative items-center p-0.5 rounded-full transition-colors bg-n-alpha-2 outline outline-1 outline-n-weak"
      :class="
        isInteractive
          ? 'cursor-pointer hover:outline-n-slate-6'
          : 'cursor-not-allowed opacity-60'
      "
      @click="toggle"
    >
      <span
        class="absolute rounded-full transition-transform duration-200 ease-out bg-n-solid-1 shadow-sm inset-y-0.5 w-[calc(50%-2px)]"
        :class="isHuman ? 'translate-x-full' : 'translate-x-0'"
      />
      <span
        class="relative z-10 px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors"
        :class="isHuman ? 'text-n-slate-11' : 'text-n-teal-11'"
      >
        {{ $t('CONVERSATION.LUCI_CONTROL.LUCI') }}
      </span>
      <span
        class="relative z-10 px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors"
        :class="isHuman ? 'text-n-amber-11' : 'text-n-slate-11'"
      >
        {{ $t('CONVERSATION.LUCI_CONTROL.HUMAN') }}
      </span>
    </button>
  </div>
</template>
